import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { company } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Indian phone: 10 digits starting 6-9, optional +91 / 91 / 0 prefix
const validatePhone = (value) => {
  const digits = value.replace(/\D/g, '');
  let local = digits;
  if (local.length === 12 && local.startsWith('91')) local = local.slice(2);
  else if (local.length === 11 && local.startsWith('0')) local = local.slice(1);
  return /^[6-9]\d{9}$/.test(local);
};

const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function LeadForm({ title = 'Request a Free Callback' }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Please enter your name';
    if (!form.phone.trim()) errs.phone = 'Please enter your phone number';
    else if (!validatePhone(form.phone)) errs.phone = 'Enter a valid 10-digit mobile number';
    if (form.email.trim() && !validateEmail(form.email)) errs.email = 'Enter a valid email address';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      // Shared event id for browser pixel <-> server Conversions API dedup
      const eventId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await axios.post(`${API}/inquiries`, { ...form, source: 'landing-page', oai_event_id: eventId });
      setForm({ name: '', phone: '', email: '', message: '' });
      setErrors({});
      // Redirect to the dedicated Thank-You page (fires conversions there)
      navigate('/thanks', { state: { eventId } });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again, or call us directly at ' + company.phone,
      });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `h-12 bg-slate-50 ${errors[field] ? 'border-red-400 focus-visible:ring-red-400' : 'border-slate-200'}`;

  return (
    <form onSubmit={submit} noValidate className="space-y-3.5">
      {title && (
        <div className="mb-1">
          <h3 className="text-xl font-bold text-slate-900 font-display">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">Get expert consultation today &mdash; 100% free.</p>
        </div>
      )}

      <div>
        <Input placeholder="Name*" value={form.name} onChange={set('name')} className={inputClass('name')} />
        {errors.name && <p className="text-xs text-red-500 mt-1 ml-1">{errors.name}</p>}
      </div>

      <div>
        <Input
          placeholder="Number*"
          type="tel"
          inputMode="tel"
          maxLength={15}
          value={form.phone}
          onChange={set('phone')}
          className={inputClass('phone')}
        />
        {errors.phone && <p className="text-xs text-red-500 mt-1 ml-1">{errors.phone}</p>}
      </div>

      <div>
        <Input placeholder="Email ID" type="email" value={form.email} onChange={set('email')} className={inputClass('email')} />
        {errors.email && <p className="text-xs text-red-500 mt-1 ml-1">{errors.email}</p>}
      </div>

      <Textarea placeholder="Message" value={form.message} onChange={set('message')} className="bg-slate-50 border-slate-200 min-h-[90px]" />

      <Button type="submit" disabled={loading} className="w-full h-12 bg-accent hover:bg-accent/90 text-white font-semibold rounded-lg text-base">
        {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</> : <><Send className="w-5 h-5 mr-2" /> Request Callback</>}
      </Button>
      <p className="text-[11px] text-center text-muted-foreground">By submitting you agree to be contacted by {company.name}.</p>
    </form>
  );
}
